import { ApplicationElement, html } from '@beunity/lit-components';
import '@beunity/lit-components/components/img_placeholder/element.js';

import translations from './translations.js';
import style from './style.lit.css';

/**
 * Link to a folder.
 *
 * @prop createdAt - Folder's creation date
 * @prop ownerInfo - Name of the folder owner (optional)
 * @prop sizeInfo - Size of the folder contents (optional)
 * @prop imageOn - Should show folder's image? If `imageSrc` is not defined, shows a placeholder. This image is only visible in "thumb" layout (optional)
 * @prop imageSrc - Url of the image next to the folder icon (optional)
 * @prop url - URL to the folders contents
 * @prop layout - The layout of the element (options: thumb, list)
 * @prop isTopLevel - Is this folder a top level folder?
 *
 * @slot default - Folder title
 */

export default class Folder extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  /** @internal */
  static translations = translations;

  static properties = {
    createdAt: { type: Date },
    sizeInfo: { type: String },
    ownerInfo: { type: String },
    imageSrc: { type: String },
    url: { type: String },
    imageOn: { type: Boolean },
    isTopLevel: { type: Boolean },
    layout: { type: String, reflect: true },
  };

  constructor() {
    super();
    this.layout = 'thumb';
  }

  /** @internal */
  get icon() {
    if (this.isTopLevel)
      return this.layout === 'list'
        ? 'folder-user:solid'
        : 'folder-closed:solid';

    return 'folder:solid';
  }

  renderThumb() {
    return html`
      <div class="figure-wrapper">
        <bu-icon name=${this.icon}></bu-icon>
        ${this.imageOn ? this.renderThumbImage() : null}
      </div>
      <div class="bottom">
        <slot></slot>
      </div>
    `;
  }

  renderThumbImage() {
    return html`
      <ef-img-placeholder picture=${this.imageSrc}></ef-img-placeholder>
    `;
  }

  renderTopLevelList() {
    return html`
      <bu-icon name=${this.icon}></bu-icon>
      <slot></slot>
    `;
  }

  renderList() {
    return html`
      <bu-icon name=${this.icon}></bu-icon>
      <div>
        <slot></slot>
        <span class="owner">${this.ownerInfo}</span>
        <span class="created-at">
          <span>${this.t('created_at')}</span>
          ${this.i18n.l('date.formats.medium', this.createdAt)}
        </span>
        <span class="size">${this.sizeInfo}</span>
      </div>
    `;
  }

  renderContent() {
    if (this.layout === 'list') {
      if (this.isTopLevel) return this.renderTopLevelList();

      return this.renderList();
    }

    return this.renderThumb();
  }

  render() {
    return html`
      <a
        href=${this.url}
        class=${this.isTopLevel ? 'level-top' : 'level-inner'}>
        ${this.renderContent()}
      </a>
    `;
  }
}

Folder.registerTagName('bu-folder');

customElements.get('bu-folder') || customElements.define('bu-folder', Folder);
